 
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getEmployeeOverview, getMyEmployeeTasks } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { WorkReportDialog } from '../../components/WorkReportDialog';
import { ENTRY_STATUS, PROJECT_STATUS, formatAmount, formatDate } from '../../lib/workStatus';
import { Briefcase, ClipboardList, MapPin, Send, AlertTriangle, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const EmployeeHome = () => {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportOpen, setReportOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [data, taskData] = await Promise.all([getEmployeeOverview(), getMyEmployeeTasks()]);
      setOverview(data);
      setTasks(taskData || []);
    } catch (error) {
      toast.error('Neizdevās ielādēt datus');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="employee-home-loading">
        <div className="w-8 h-8 border-4 border-[#1F3D2B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="employee-home">
      <div>
        <h1 className="text-2xl font-bold text-[#1F3D2B]" data-testid="employee-home-title">Sveiki, {user?.client_name?.split(' ')[0] || 'kolēģi'}!</h1>
        <p className="text-[#2E2E2E]/70">Jums ir <strong data-testid="employee-home-open-tasks">{overview?.task_open || 0}</strong> aktīvi darba uzdevumi.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="employee-home-actions">
        <Button asChild className="h-16 text-base bg-[#1F3D2B] hover:bg-[#2a5039] justify-start px-5" data-testid="employee-home-tasks-btn">
          <Link to="/portal/employee/tasks"><Briefcase className="w-5 h-5 mr-3" /> Mani darba uzdevumi</Link>
        </Button>
        <Button onClick={() => setReportOpen(true)} className="h-16 text-base bg-green-700 hover:bg-green-800 justify-start px-5" data-testid="employee-home-report-btn">
          <Send className="w-5 h-5 mr-3" /> Iesniegt paveikto darbu
        </Button>
      </div>

      {overview?.needs_fix > 0 && (
        <Link to="/portal/employee/entries" className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-800" data-testid="employee-home-needs-fix">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1 text-sm">Administrators lūdz precizēt {overview.needs_fix} atskaiti(-es).</span>
          <ChevronRight className="w-4 h-4" />
        </Link>
      )}

      <section className="space-y-3" data-testid="employee-home-projects">
        <h2 className="text-lg font-semibold text-[#1F3D2B] flex items-center gap-2"><MapPin className="w-5 h-5" /> Mani objekti</h2>
        {overview?.projects?.length ? overview.projects.map((project) => {
          const status = PROJECT_STATUS[project.status] || { label: project.status, className: 'bg-gray-100 text-gray-700' };
          return (
            <Card key={project.id} className="border-0 shadow-sm" data-testid={`employee-project-${project.id}`}>
              <CardContent className="p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-[#2E2E2E] truncate">{project.property_name || project.name}</p>
                  <p className="text-sm text-[#2E2E2E]/60">
                    {project.area_ha ? `Platība: ${formatAmount(project.area_ha, 'ha')}` : 'Platība nav norādīta'}
                    {project.cadastral_number ? ` · ${project.cadastral_number}` : ''}
                  </p>
                  <p className="text-xs text-[#2E2E2E]/50">{project.open_tasks} aktīvi / {project.task_count} uzdevumi</p>
                </div>
                <Badge className={status.className}>{status.label}</Badge>
              </CardContent>
            </Card>
          );
        }) : (
          <Card className="border-0 shadow-sm"><CardContent className="py-8 text-center text-[#2E2E2E]/60" data-testid="employee-home-projects-empty">Pašlaik nav piešķirtu objektu.</CardContent></Card>
        )}
      </section>

      <section className="space-y-3" data-testid="employee-home-recent">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[#1F3D2B] flex items-center gap-2"><ClipboardList className="w-5 h-5" /> Pēdējās atskaites</h2>
          <Link to="/portal/employee/entries" className="text-sm text-[#1F3D2B] hover:underline" data-testid="employee-home-entries-link">Visas</Link>
        </div>
        {overview?.recent_entries?.length ? overview.recent_entries.slice(0, 5).map((entry) => {
          const status = ENTRY_STATUS[entry.status] || { label: entry.status, className: '' };
          return (
            <div key={entry.id} className="flex items-center justify-between rounded-xl bg-white p-3 shadow-sm" data-testid={`employee-recent-entry-${entry.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{entry.task_title}</p>
                <p className="text-xs text-[#2E2E2E]/60">{formatDate(entry.date)} · {formatAmount(entry.amount, entry.unit)}{entry.completed ? ' · pabeigts' : ''}</p>
              </div>
              <Badge className={status.className}>{status.label}</Badge>
            </div>
          );
        }) : <p className="text-sm text-[#2E2E2E]/60" data-testid="employee-home-recent-empty">Vēl nav iesniegtu atskaišu.</p>}
      </section>

      <WorkReportDialog open={reportOpen} onOpenChange={setReportOpen} tasks={tasks} onSubmitted={load} />
    </div>
  );
};

export default EmployeeHome;
